import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useResource } from '../../administration/components';
import type { AdminContext } from '../../administration/api';
import { EinstellungsGruppe, einstellungsGruppen } from './seiten';

// Die Gruppen der Einstellungen für Konto- und Firmen-Seite (Profil-Nachbau F-02, F-11; BAU-FIRMA.md 6.1). Kopie von
// Einstellungen.tsx:90-104 (Stand 15a71bab); Pop-up (Einstellungen.tsx) und Konto-Seite (KontoSeite.tsx) nutzen den Hook;
// LETZTER_KONTEXT gilt für beide.

// Plan 1a Welle 1, G3-6 (4.04): der zuletzt geladene Firmenkontext je Firma (und Sicht), solange die Seite offen ist.
const LETZTER_KONTEXT = new Map<string, AdminContext>();

export function useEinstellungsGruppen(): {
  gruppen: EinstellungsGruppe[];
  firmenKontext: AdminContext | undefined;
  /** Die Firmenbereiche laden zum ersten Mal. */
  bereicheLaden: boolean;
  fehler: boolean;
  /** Neuer Versuch, den Firmenkontext zu laden. */
  nochmals: () => void;
} {
  const { user } = useAuth();
  const brand = useBrand();
  // Die Bereiche der Organisation nennt der Kontext der Firmenebene; ohne Admin-Einstellungen wird er nicht gefragt.
  const [versuch, setVersuch] = useState(0);
  const kontext = useResource<AdminContext>(user?.ebenen?.adminEinstellungen ? '/context?ebene=firma' : null, versuch);
  // Plan 1a Welle 1, G3-6 (W1-16, 4.04): der zuletzt geladene Firmenkontext bleibt je Firma stehen, bis der neue da ist;
  // beim allerersten Laden sagt die Gruppe, dass die Bereiche geladen werden. In einer Sicht gilt der Kontext der Sicht
  // (Schlüssel mit Linse und Firma).
  const firmaId = user?.ebenen?.adminEinstellungen?.firmaId;
  const kontextSchluessel = firmaId ? `${firmaId}|${user?.sicht?.aktiv ? `${user.sicht.aktiv.linse}:${user.sicht.aktiv.firmaName}` : ''}` : '';
  if (kontext.data && kontextSchluessel) LETZTER_KONTEXT.set(kontextSchluessel, kontext.data);
  const firmenKontext = kontext.data ?? (kontextSchluessel && !kontext.error ? LETZTER_KONTEXT.get(kontextSchluessel) : undefined);
  const bereicheLaden = !!firmaId && !firmenKontext && !kontext.error;
  // Plan 1a Welle 0 (W-04) und 1A-1 (Bennys Antwort 09.10.2026): „Abrechnung & Kosten“ nur beim Einzelkonto, ohne die
  // Abrechnung der eigenen Firma auch „Tarife“.
  const gruppen = einstellungsGruppen({ ebenen: user?.ebenen, sicht: user?.sicht, firmenKontext, produkt: brand.displayName,
    konto: { organisationId: user?.access?.organization?.id, canOpenBilling: user?.account?.canOpenBilling, einzelkonto: user?.access?.organization?.kind === 'SOLO' } });
  return { gruppen, firmenKontext, bereicheLaden, fehler: !!kontext.error, nochmals: () => setVersuch(v => v + 1) };
}
